import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { XCircle, ArrowLeft, RefreshCw, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

/**
 * Página de pago cancelado
 *
 * Muestra información cuando el usuario cancela el pago en Transbank
 */
export function PaymentCancelledPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Obtener parámetros de la URL
  const reason = searchParams.get("reason");
  const order = searchParams.get("order");

  useEffect(() => {
    // Simular carga de datos
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const getReasonMessage = () => {
    if (reason === "user_cancelled") {
      return "Cancelaste el pago en la pasarela de Transbank";
    }
    if (reason === "timeout") {
      return "El tiempo para completar el pago expiró";
    }
    return "El proceso de pago fue cancelado";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Procesando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver al Inicio
            </Button>
          </div>
        </div>
      </div>

      {/* Contenido Principal */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Mensaje de Cancelación */}
          <Card className="mb-6 border-yellow-200 bg-yellow-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <XCircle className="h-8 w-8 text-yellow-600" />
                <div>
                  <h1 className="text-2xl font-bold text-yellow-800">
                    Pago Cancelado
                  </h1>
                  <p className="text-yellow-600">{getReasonMessage()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detalles de la Cancelación */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Información
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Estado
                  </label>
                  <Badge variant="outline" className="mt-1 bg-yellow-50 text-yellow-700 border-yellow-300">
                    Cancelado
                  </Badge>
                </div>
                {order && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Orden
                    </label>
                    <p className="text-sm font-mono mt-1">{order}</p>
                  </div>
                )}
              </div>

              <Separator />

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-medium text-blue-800 mb-2">
                  Tu carrito se mantiene intacto
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  No se realizó ningún cobro. Puedes volver a intentar el pago cuando estés listo.
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Verifica que tengas los fondos suficientes</li>
                  <li>• Asegúrate de completar el pago dentro del tiempo límite</li>
                  <li>• Si el problema persiste, contacta a tu banco</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Acciones */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => navigate("/checkout")}
              className="flex-1"
              size="lg"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Intentar Nuevamente
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="flex-1"
              size="lg"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a la Tienda
            </Button>
          </div>

          {/* Información Adicional */}
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Si tienes alguna pregunta, contacta a nuestro soporte técnico
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
